import { DeliveryBlock, Driver, TaskStatus, Vehicle } from '@bringg/types';
import type { ExtendedTimelineItem, GanttTimeLineItem, GroupRow } from '@bringg-frontend/gantt-timeline';
import type { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';
import { BringgFontIcons } from '@bringg/bringg-icons';
import classNames from 'classnames';

import type { RoutesPlannerBreak } from '../components/routes-planner/routes-planner';
import { etaRoutesConverter } from '../services/eta/eta-routes-converter';
import { tasksRangeTime } from '../services/task/tasks-range-time';
import type { RoutesPlannerRun, RoutesPlannerTask } from '../types';
import { isLate } from '../components/routes-planner/timeline-logic/tasks-logic';

export const DISABLED_ITEM_CLASS_NAME = 'editable-false';
export const SECOND_IN_MS = 1000;
const MINUTE_IN_SEC = 60;
export const MINUTE_IN_MS = SECOND_IN_MS * MINUTE_IN_SEC;

export enum GanttItems {
	DELIVERY_BLOCK = 'delivery_block',
	TASK = 'task',
	BREAK = 'break',
	CONSECUTIVE_ROUTE = 'consecutive_route'
}

export const createGanttRow = (
	runId: number,
	runName: string,
	driverOfRun: Driver,
	vehicleOfRun: Vehicle,
	shouldShowVehicles
): GroupRow => {
	const rowCells = [runName, driverOfRun?.name || ''];

	if (shouldShowVehicles) {
		rowCells.push(vehicleOfRun?.name || '');
	}

	return {
		rowId: runId,
		rowCells
	};
};

export const getClusterContent = (timelineItem: ExtendedTimelineItem) => {
	if (timelineItem.customData?.type === GanttItems.CONSECUTIVE_ROUTE) {
		return (timelineItem.content = `${timelineItem.customData.consecutiveRoutesTranslation} (${timelineItem.customData.countItems})`);
	}
	return (timelineItem.content += '+');
};

export const isTaskStarted = (taskStatus?: TaskStatus): boolean =>
	taskStatus !== undefined && ![TaskStatus.Created, TaskStatus.Assigned, TaskStatus.Accepted].includes(taskStatus);

const isTaskEditable = (taskStatus?: TaskStatus): boolean => !isTaskStarted(taskStatus);

export const taskToGanttItem = (task: RoutesPlannerTask, color: string): GanttTimeLineItem => {
	const wayPoint = task.way_points[0];
	const isEditable = isTaskEditable(task.status);
	const editable = isEditable
		? {
				draggable: isEditable,
				draggableToOtherRow: isEditable
		  }
		: false;

	const { start, end } = tasksRangeTime.getTaskRange(task);

	return {
		id: task.id,
		rowId: Number(task.run_id),
		start,
		end,
		content: task.priority.toString(),
		color: color,
		className: !isEditable ? DISABLED_ITEM_CLASS_NAME : '',
		editable,
		customData: {
			noEarlierThan: wayPoint.no_earlier_than,
			noLaterThan: wayPoint.no_later_than,
			late: isLate(wayPoint),
			linkedTaskId: task.linked_task_id,
			type: GanttItems.TASK
		}
	} as GanttTimeLineItem;
};

export const BREAK_CLASS_NAME = 'gantt-break';
export const breakToGanttItems = (
	breaks: RoutesPlannerBreak[],
	color: string,
	breakText: string
): GanttTimeLineItem[] => {
	return breaks.map(
		brk =>
			({
				id: `${brk.position}_${brk.run_id}`,
				rowId: brk.run_id,
				start: brk.estimated_start_time,
				end: brk.estimated_end_time,
				content: breakText,
				color,
				className: BREAK_CLASS_NAME,
				clusterable: false,
				clickable: false,
				editable: {
					draggable: false,
					draggableToOtherRow: false
				},
				customData: {
					type: GanttItems.BREAK,
					icon: BringgFontIcons.CafeSolid
				}
			} as GanttTimeLineItem)
	);
};

export const DELIVERY_BLOCK_NAME = 'gantt-delivery-block';
export const deliveryBlockToGanttItem = (deliveryBlock: DeliveryBlock, runId: number): GanttTimeLineItem => {
	return {
		id: `${runId}${deliveryBlock.id}`,
		rowId: Number(runId),
		start: deliveryBlock.start_time,
		end: deliveryBlock.end_time,
		color: 'rgba(160, 174, 192, 0.1)',
		className: DELIVERY_BLOCK_NAME,
		clusterable: false,
		clickable: false,
		customData: {
			itemType: 'background'
		},
		content: '',
		editable: {
			draggable: false,
			draggableToOtherRow: false
		}
	} as GanttTimeLineItem;
};

export const consecutiveRouteToGanttItem = (
	run: RoutesPlannerRun | EtaResponseRouteItem,
	runId: number,
	translations: { consecutiveRoute: string; consecutiveRoutes: string }
): GanttTimeLineItem => {
	return {
		id: Math.floor(Math.random() * 100000),
		rowId: runId,
		start: new Date(run.depot_etl * SECOND_IN_MS).toString(),
		end: new Date(run.depot_back_eta * SECOND_IN_MS).toString(),
		content: translations.consecutiveRoute,
		color: 'rgba(160, 174, 192, 0.8)',
		clusterable: true,
		clickable: true,
		customData: {
			consecutiveRouteId: (run as RoutesPlannerRun).id ?? (run as EtaResponseRouteItem).run_id,
			consecutiveRoutesTranslation: translations.consecutiveRoutes,
			type: GanttItems.CONSECUTIVE_ROUTE
		},
		editable: {
			draggable: false,
			draggableToOtherRow: false
		}
	} as GanttTimeLineItem;
};

export const LOADING_TIME_CLASS_NAME = 'gantt-loading-time';
export const UNLOADING_TIME_CLASS_NAME = 'gantt-unloading-time';
export const rechargeTimeToGanttItem = (
	run: RoutesPlannerRun | EtaResponseRouteItem,
	timeInMinutes: number,
	isLoading: boolean
): GanttTimeLineItem => {
	let startInMs, endInMs: number;
	if (isLoading) {
		const runStartInMs = run.depot_etl * SECOND_IN_MS;
		startInMs = runStartInMs - timeInMinutes * MINUTE_IN_MS;
		endInMs = runStartInMs;
	} else {
		const runEndInMs = run.depot_back_eta * SECOND_IN_MS;
		startInMs = runEndInMs;
		endInMs = runEndInMs + timeInMinutes * MINUTE_IN_MS;
	}
	const runId = etaRoutesConverter.getRouteId(run);

	return {
		id: `${runId}-${isLoading ? '' : 'un'}loadingTime`,
		rowId: runId,
		start: new Date(startInMs).toString(),
		end: new Date(endInMs).toString(),
		className: classNames(LOADING_TIME_CLASS_NAME, { [UNLOADING_TIME_CLASS_NAME]: !isLoading }),
		clusterable: false,
		clickable: false
	} as GanttTimeLineItem;
};
